import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Flag, X, Trophy, AlertTriangle, Coins, Zap, Loader2 } from "lucide-react";

/**
 * Dialog "Finalizza Stagione" — bulk close di tutte le sponsorship attive.
 * - Fetch preview (calcolo per team+sponsor con classifica corrente)
 * - Doppia conferma: (1) apertura dialog con preview, (2) checkbox "Confermo" + tasto azione
 */
export default function FinalizeSeasonDialog({ leagueId, open, onClose, onDone }) {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [executing, setExecuting] = useState(false);

  useEffect(() => {
    if (!open) { setPreview(null); setConfirm(false); return; }
    (async () => {
      setLoading(true);
      try {
        const r = await api.post(`/leagues/${leagueId}/sponsors/finalize/preview`, {});
        setPreview(r.data);
      } catch (e) {
        toast.error(formatApiError(e));
        onClose?.();
      } finally { setLoading(false); }
    })();
  }, [open, leagueId, onClose]);

  const execute = async () => {
    if (!confirm) return;
    setExecuting(true);
    try {
      const r = await api.post(`/leagues/${leagueId}/sponsors/finalize/confirm`, {
        confirm: true, only_computable: true,
      });
      toast.success(`✅ Chiuse ${r.data.count_settled} sponsorship${r.data.count_skipped > 0 ? ` · ${r.data.count_skipped} saltate` : ""}`);
      onDone?.(r.data);
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setExecuting(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur"
      data-testid="finalize-dialog"
      onClick={(e) => { if (e.target === e.currentTarget && !executing) onClose?.(); }}>
      <div className="w-full max-w-3xl max-h-[92vh] overflow-hidden bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <Flag className="text-amber-400" size={18}/>
            <div>
              <div className="font-black uppercase tracking-widest text-white text-sm">Finalizza stagione</div>
              <div className="text-[10px] text-slate-500">Chiude tutti gli sponsor attivi e paga premi/scommesse</div>
            </div>
          </div>
          <button onClick={onClose} disabled={executing} className="text-slate-400 hover:text-white disabled:opacity-30" data-testid="close-finalize">
            <X size={18}/>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading && (
            <div className="flex items-center gap-2 text-slate-400 text-sm p-6 justify-center">
              <Loader2 className="animate-spin" size={16}/> Calcolo preview…
            </div>
          )}

          {preview && (
            <>
              {/* Totals */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Kpi label="Sponsorship" value={preview.count_active} tone="slate"/>
                <Kpi label="Premi" value={`+${preview.totals.prize}c`} tone="emerald" icon={<Trophy size={11}/>}/>
                <Kpi label="Penalità" value={`−${preview.totals.penalty}c`} tone="rose" icon={<AlertTriangle size={11}/>}/>
                <Kpi label="Rimborso bet 2x" value={`+${preview.totals.bet_payout}c`} tone="amber" icon={<Zap size={11}/>}/>
              </div>
              <div className="text-xs text-slate-400 italic">
                Fonte posizioni: <span className="font-mono text-slate-300">{preview.positions_source === "current_standings" ? "classifica corrente" : "manuale"}</span>
                {" · "}Computabili: <span className="font-bold text-white">{preview.count_computable}/{preview.count_active}</span>
              </div>

              {/* Items */}
              <div className="rounded-xl border border-slate-800 max-h-72 overflow-y-auto" data-testid="finalize-items">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-slate-900/95 backdrop-blur">
                    <tr className="text-[9px] uppercase tracking-widest text-slate-500 border-b border-slate-800">
                      <th className="text-left px-2 py-1.5">Team</th>
                      <th className="text-left px-2 py-1.5">Sponsor</th>
                      <th className="text-right px-2 py-1.5">Pos</th>
                      <th className="text-right px-2 py-1.5">%</th>
                      <th className="text-right px-2 py-1.5">Premio</th>
                      <th className="text-right px-2 py-1.5">Bet</th>
                      <th className="text-right px-2 py-1.5">Δ Budget</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.items.map(it => (
                      <tr key={it.sponsorship_id} className="border-t border-slate-800"
                        data-testid={`finalize-item-${it.sponsorship_id}`}>
                        <td className="px-2 py-1 text-white font-bold truncate max-w-[120px]">{it.team_name}</td>
                        <td className="px-2 py-1">
                          <span className={it.category === "bonus_initial" ? "text-emerald-300" : "text-amber-300"}>
                            {it.sponsor_name}
                          </span>
                        </td>
                        <td className="px-2 py-1 text-right font-mono">
                          {it.final_position ?? <span className="text-rose-400">—</span>}
                        </td>
                        <td className="px-2 py-1 text-right font-mono">
                          {it.percent != null ? `${it.percent}%` : "—"}
                        </td>
                        <td className="px-2 py-1 text-right font-mono text-emerald-300">
                          {it.prize > 0 ? `+${it.prize}` : (it.penalty > 0 ? <span className="text-rose-300">−{it.penalty}</span> : "0")}
                        </td>
                        <td className="px-2 py-1 text-right font-mono text-amber-300">
                          {it.bet_amount > 0 ? (it.bet_payout > 0 ? `+${it.bet_payout} ✅` : `−${it.bet_amount} ❌`) : "—"}
                        </td>
                        <td className={`px-2 py-1 text-right font-mono font-black ${it.delta_budget > 0 ? "text-emerald-300" : it.delta_budget < 0 ? "text-rose-300" : "text-slate-500"}`}>
                          {it.delta_budget > 0 ? `+${it.delta_budget}` : it.delta_budget}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Net delta */}
              <div className="flex items-center justify-between p-3 rounded-xl border border-amber-500/40 bg-amber-500/10">
                <div className="flex items-center gap-2 text-sm">
                  <Coins className="text-amber-400" size={14}/>
                  <span className="font-black uppercase tracking-widest text-amber-300 text-xs">Netto totale</span>
                </div>
                <div className={`text-xl font-black font-mono ${preview.totals.net_delta >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                  {preview.totals.net_delta >= 0 ? "+" : ""}{preview.totals.net_delta}c
                </div>
              </div>

              {/* Warning */}
              <div className="rounded-xl bg-rose-500/10 border border-rose-500/30 p-3 flex items-start gap-2 text-xs text-rose-200">
                <AlertTriangle size={14} className="text-rose-300 shrink-0 mt-0.5"/>
                <div>
                  <div className="font-bold text-rose-100">Operazione irreversibile</div>
                  <div>Chiude in blocco tutte le sponsorship attive: prizes, penalità e rimborsi bet 2x verranno applicati ai budget dei team. Non si può annullare (solo storno manuale). Le sponsorship senza posizione in classifica verranno saltate.</div>
                </div>
              </div>

              {/* Confirmation checkbox */}
              <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg border border-slate-700 bg-slate-950">
                <input type="checkbox" checked={confirm} onChange={e => setConfirm(e.target.checked)}
                  data-testid="finalize-confirm-check"
                  className="w-4 h-4 accent-amber-500"/>
                <span className="text-sm text-slate-200">Ho controllato i valori e voglio finalizzare la stagione</span>
              </label>

              {preview.count_computable === 0 && (
                <div className="text-xs text-rose-300 flex items-start gap-1.5 pl-1" data-testid="finalize-nocomputable">
                  <AlertTriangle size={12} className="mt-0.5"/>
                  <span>Nessuna sponsorship computabile: importa prima la classifica dalla tab <span className="font-bold">Classifica</span>.</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-800 bg-slate-900/70">
          <button onClick={onClose} disabled={executing}
            className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-300 border border-slate-700 hover:border-slate-500 disabled:opacity-30"
            data-testid="cancel-finalize">Annulla</button>
          <button onClick={execute} disabled={!confirm || !preview || executing || (preview?.count_computable === 0)}
            data-testid="execute-finalize"
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-widest bg-gradient-to-br from-amber-400 to-amber-600 text-slate-900 hover:brightness-110 disabled:opacity-30 shadow-lg shadow-amber-500/20">
            {executing ? <><Loader2 className="animate-spin" size={13}/> Chiudo…</> : <><Flag size={13}/> Finalizza stagione</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, tone = "slate", icon = null }) {
  const map = {
    slate: "border-slate-800 bg-slate-950/60 text-slate-100",
    emerald: "border-emerald-500/40 bg-emerald-500/10 text-emerald-100",
    rose: "border-rose-500/40 bg-rose-500/10 text-rose-100",
    amber: "border-amber-500/40 bg-amber-500/10 text-amber-100",
  };
  return (
    <div className={`rounded-xl border p-2 ${map[tone]}`}>
      <div className="text-[9px] uppercase tracking-widest text-slate-400 font-black flex items-center gap-1">{icon}{label}</div>
      <div className="text-lg font-black tabular-nums">{value}</div>
    </div>
  );
}
